import { useState } from 'react'
import { UploadOutlined } from '@ant-design/icons'
import { Button, Upload, Modal, App } from 'antd'
import { useTranslation } from 'react-i18next'
import Spinner from '$components/Spinner'
import * as XLSX from 'xlsx'
import { useQueryClient, useMutation } from '@tanstack/react-query'
import { createClientMaterials } from '$api/client'
import Tooltip from '$components/Tooltip'
import PropTypes from 'prop-types'

const UploadExcelButton = ({ id }) => {
  const { t } = useTranslation()

  const { notification } = App.useApp()

  const [loading, setLoading] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [items, setItems] = useState([])

  const queryClient = useQueryClient()

  const formatData = (data) =>
    data.map((obj) => {
      const key = Object.keys(obj)[0]
      return { name: obj[key] } // dynamic value
    })

  const readExcel = async (file) => {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader()
      fileReader.readAsArrayBuffer(file)

      fileReader.onload = (e) => {
        const bufferArray = e.target.result
        const wb = XLSX.read(bufferArray)
        const wsname = wb.SheetNames[0]
        let ws = wb.Sheets[wsname]

        const data = XLSX.utils.sheet_to_json(ws)

        const formattedData = formatData(data)

        resolve(formattedData)
      }

      fileReader.onerror = (error) => {
        reject(error)
      }
    })
  }

  const addMaterialsMutation = useMutation(createClientMaterials, {
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['clientMaterialsList'] })
      queryClient.invalidateQueries({
        queryKey: ['allClientMaterialsForCheck'],
      })
      queryClient.invalidateQueries({ queryKey: ['clientMaterialsForKPIs'] })
      setLoading(false)
      notification.success({
        message: t('my_materials.materials_added', {
          number: data.created_materials.length,
        }),
      })
      data.created_materials.length < items.length &&
        notification.info({
          message: t('my_materials.ignored_materials_message'),
        })
      data.skipped_invalid_materials.length > 0 &&
        notification.warning({
          message: t('my_materials.invalid_materials_skipped', {
            names: data.skipped_invalid_materials.join(', '),
          }),
        })
    },
    onError: (error) => {
      setLoading(false)
      console.error(error)
      if (error.response && error.response.status === 409) {
        notification.error({
          message: t('my_materials.demo_excel_limit'),
        })
      } else {
        notification.error({ message: t('error_adding_material') })
      }
    },
  })

  const handleUpload = async (file) => {
    const items = await readExcel(file)
    setIsModalOpen(true)
    setItems(items)
  }

  const handleOk = () => {
    setIsModalOpen(false)
    setLoading(true)

    addMaterialsMutation.mutate(items)
  }

  const handleCancel = () => {
    setIsModalOpen(false)
  }

  if (loading) {
    return (
      <div
        style={{
          textAlign: 'center',
          marginLeft: 'auto',
          marginRight: 'auto',
          verticalAlign: 'middle',
          padding: '25% 0',
        }}
      >
        <Spinner />
        {t('my_materials.upload_in_progress')}
      </div>
    )
  }

  return (
    <>
      <Upload
        name="file"
        accept=".xlsx"
        maxCount={1}
        beforeUpload={(info) => {
          const isXlsx =
            info.type ===
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
            info.name.endsWith('.xlsx') // fallback check

          if (!isXlsx) {
            notification.error({ message: t('my_materials.file_type_error') })
            return Upload.LIST_IGNORE
          }
          handleUpload(info)

          // always return false to disable default upload as we are handling that differently
          return false
        }}
        itemRender={(originNode, _file) => {
          return (
            <div
              style={{
                maxWidth: 80,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {originNode}
            </div>
          )
        }}
      >
        <Tooltip
          placement="bottom"
          title={t('my_materials.import_list_tooltip')}
        >
          <Button
            id={id}
            icon={<UploadOutlined />}
            type="primary"
            size="medium"
            style={{ borderRadius: '10px 0 0 10px' }}
          >
            {t('my_materials.import_button')}
          </Button>
        </Tooltip>
      </Upload>
      <Modal
        destroyOnClose="true"
        centered="true"
        title={t('my_materials.upload_confirmation_modal')}
        open={isModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        maskClosable={false}
        okText={t('add_button')}
        cancelText={t('cancel_button')}
      />
    </>
  )
}

UploadExcelButton.propTypes = {
  id: PropTypes.string,
}

export default UploadExcelButton
